// One exchange, and the only thing that makes it worth anything: saving it.
//
// Nothing is saved automatically. A studio that quietly fills the draft library
// with everything it ever produced is a library nobody trusts and nobody opens,
// and the picker in Compose is only useful if what is in it was chosen.

import { useState } from "react";
import { saveDraft } from "../lib/api";
import { Spinner } from "../components/Spinner";
import type { AiGeneration, DraftKind } from "../lib/types";

const KINDS: Array<{ key: DraftKind; label: string }> = [
  { key: "caption", label: "Caption" },
  { key: "description", label: "Description" },
  { key: "punchline", label: "Punch line" },
  { key: "title", label: "Title" },
];

/** What this intent most likely becomes, so the common case is one click. */
const DEFAULT_KIND: Record<string, DraftKind> = {
  post: "caption",
  description: "description",
  punchline: "punchline",
  freeform: "caption",
};

export function Turn({
  turn, workspaceId, canSave, onRegenerate,
}: {
  turn: AiGeneration;
  workspaceId: string;
  canSave: boolean;
  onRegenerate: (brief: string) => void;
}) {
  const [kind, setKind] = useState<DraftKind>(DEFAULT_KIND[turn.intent] ?? "caption");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!turn.output) return;
    setSaving(true);
    setError(null);
    try {
      await saveDraft({
        workspaceId,
        kind,
        body: turn.output,
        source: "ai",
        generationId: turn.id,
      });
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function copy() {
    if (!turn.output) return;
    try {
      await navigator.clipboard.writeText(turn.output);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // A blocked clipboard is not worth an error card; the text is on screen
      // and selectable, which is the fallback everyone already knows.
      setError("Your browser would not let us copy. Select the text instead.");
    }
  }

  return (
    <div className="studio-turn">
      <p className="studio-brief">{turn.input}</p>

      {turn.status === "failed" ? (
        <div className="notice">
          That one did not come back, and nothing was charged.{" "}
          {turn.error && <span className="muted">{turn.error}</span>}
          <div className="row" style={{ marginTop: ".6rem" }}>
            <button onClick={() => onRegenerate(turn.input)}>Try again</button>
          </div>
        </div>
      ) : (
        <>
          <div className="studio-output">{turn.output}</div>

          <div className="row studio-turn-actions">
            {canSave && (
              <>
                <label className="visually-hidden" htmlFor={`kind-${turn.id}`}>Save as</label>
                <select
                  id={`kind-${turn.id}`}
                  value={kind}
                  onChange={(e) => { setKind(e.target.value as DraftKind); setSaved(false); }}
                >
                  {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
                <button onClick={save} disabled={saving || saved}>
                  {saving && <Spinner />} {saved ? "Saved" : saving ? "Saving…" : "Save"}
                </button>
              </>
            )}
            <button onClick={copy}>{copied ? "Copied" : "Copy"}</button>
            <button onClick={() => onRegenerate(turn.input)}>Write another</button>
            <span className="spacer" />
            <span className="muted studio-turn-meta">
              {turn.billing === "byok" ? "Your own key" : `${turn.credits} credits`}
            </span>
          </div>

          {saved && (
            <p className="muted studio-saved">
              Saved. It is in “Use a saved draft” on the Compose screen now.
            </p>
          )}
          {error && <p className="err">{error}</p>}
        </>
      )}
    </div>
  );
}
